/**
 * "Jump to the newest message" — the button that appears once the reader has
 * scrolled away from the bottom of a transcript.
 *
 * A chat that auto-scrolls while you are reading further up is hostile, and one
 * that does not tell you new text arrived below is opaque. The usual answer is
 * this: stop following when the reader scrolls up, and offer one click back.
 *
 * The button asks for its scroller through a callback rather than a ref: both
 * chat callers look their scroller up as `.messages` inside a container they
 * query — `ChatExchange` and, since it shares the chat components, the
 * question panel — and that element mounts later than the button does.
 * It positions itself absolutely, so the surrounding element needs
 * `position: relative`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface Properties {
  /** The scrolling element. Re-read on every check, since it may mount late. */
  scroller: () => HTMLElement | null | undefined;
  /**
   * How far from the bottom counts as "away", in pixels. A few pixels of slack
   * keeps the button from flickering on sub-pixel scroll positions and while
   * text streams in.
   */
  threshold?: number;
  /** Distance from the bottom edge of the scroller, in pixels. */
  bottom?: number;
  /** Changes to this value re-check the position — pass the message count. */
  watch?: unknown;
}

export default function ScrollToBottomButton(properties: Properties) {
  const { t } = useTranslation();
  const { scroller, threshold = 80, bottom = 16, watch } = properties;
  const [away, setAway] = useState(false);
  const topUpIntervalRef = useRef<number | null>(null);

  const check = useCallback(() => {
    const element = scroller();
    if (!element) {
      setAway(false);
      return;
    }
    const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
    setAway(distance > threshold);
  }, [scroller, threshold]);

  useEffect(() => {
    const element = scroller();
    if (!element) {
      // The scroller can mount after this component — look again shortly.
      const retry = setTimeout(check, 200);
      return () => clearTimeout(retry);
    }

    check();
    element.addEventListener("scroll", check, { passive: true });

    // Streaming text changes the height without a scroll event. ResizeObserver
    // watches the element's box size, which stays fixed when overflow content
    // grows, so MutationObserver detects the DOM changes that grow scrollHeight.
    const resizeObserver =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(check);
    resizeObserver?.observe(element);

    const mutationObserver =
      typeof MutationObserver === "undefined"
        ? null
        : new MutationObserver(check);
    mutationObserver?.observe(element, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      element.removeEventListener("scroll", check);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [scroller, check, watch]);

  useEffect(() => {
    return () => {
      if (topUpIntervalRef.current !== null) {
        window.clearInterval(topUpIntervalRef.current);
      }
    };
  }, []);

  if (!away) return null;

  return (
    <button
      type="button"
      aria-label={t("chat.scrollToBottom")}
      title={t("chat.scrollToBottom")}
      onClick={() => {
        const element = scroller();
        if (!element) return;

        /**
         * Scroll, then keep topping up.
         *
         * The last message often grows *after* the scroll starts: choice chips,
         * resources and follow-up suggestions mount a beat later, and a single
         * `scrollTo` lands where the bottom used to be. So the smooth scroll is
         * followed by a few instant corrections while the height settles.
         *
         * The corrections stop as soon as the reader scrolls up themselves —
         * fighting a user who has taken over is worse than stopping short.
         */
        element.scrollTo({ top: element.scrollHeight, behavior: "smooth" });

        if (topUpIntervalRef.current !== null) {
          window.clearInterval(topUpIntervalRef.current);
        }

        let previousTop = element.scrollTop;
        let attempts = 0;
        topUpIntervalRef.current = window.setInterval(() => {
          attempts += 1;
          const target = element.scrollHeight - element.clientHeight;
          const scrolledUpByUser = element.scrollTop < previousTop - 4;

          if (scrolledUpByUser || attempts > 8 || Math.abs(element.scrollTop - target) < 2) {
            if (topUpIntervalRef.current !== null) {
              window.clearInterval(topUpIntervalRef.current);
              topUpIntervalRef.current = null;
            }
            return;
          }

          element.scrollTop = target;
          previousTop = element.scrollTop;
        }, 120);
      }}
      style={{
        position: "absolute",
        left: "50%",
        transform: "translateX(-50%)",
        bottom: `${bottom}px`,
        zIndex: 5,
        width: "32px",
        height: "32px",
        borderRadius: "50%",
        border: "1px solid #edebe9",
        backgroundColor: "#fff",
        color: "#605e5c",
        cursor: "pointer",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.16)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
      }}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 5v14M19 12l-7 7-7-7" />
      </svg>
    </button>
  );
}
